import { buttonClassNames, makeResetStyles, makeStyles, mergeClasses } from '@fluentui/react-components';
import type { PromptStarterListSlots, PromptStarterListState } from './PromptStarterList.types';
import type { SlotClassNames } from '@fluentui/react-components';
import { tokens } from '@fluentui-copilot/tokens';

export const promptStarterListClassNames: SlotClassNames<PromptStarterListSlots> = {
  root: 'fai-PromptStarterList',
  gridWrapper: 'fai-PromptStarterList__gridWrapper',
  actions: 'fai-PromptStarterList__actions',
  expandButton: 'fai-PromptStarterList__expandButton',
};

/**
 * Styles for the root slot
 */
const useRootClassName = makeResetStyles({
  display: 'grid',
  gridTemplateColumns: '1fr max-content max-content',
  gridTemplateRows: 'max-content max-content',
  gridTemplateAreas: `"grid grid grid"
                      ". actions expandButton"`,
  maxWidth: '772px',
  rowGap: tokens.spacingVerticalXL,
  columnGap: tokens.spacingHorizontalSNudge,
});

const useStyles = makeStyles({
  gridWrapper: {
    gridArea: 'grid',
    display: 'grid',
    gap: tokens.spacingHorizontalL,
    gridAutoColumns: 'max-content',

    gridTemplateColumns: `repeat(auto-fit, minmax(214px, 1fr))`,
    gridAutoFlow: 'unset',
  },

  actions: {
    gridArea: 'actions',
  },

  expandButton: {
    gridArea: 'expandButton',
    [`& .${buttonClassNames.icon}`]: {
      fontSize: '12px',
      height: '12px',
      width: '12px',
    },
    ':hover': {
      [`& .${buttonClassNames.icon}`]: {
        color: tokens.colorNeutralForeground2Hover,
      },
    },
    ':hover:active': {
      [`& .${buttonClassNames.icon}`]: {
        color: tokens.colorNeutralForeground2Pressed,
      },
    },
  },

  expandButtonExpanded: {
    [`& > .${buttonClassNames.icon}`]: {
      transform: 'rotate(180deg)',
    },
  },
});

/**
 * Apply styling to the PromptStarterList slots based on the state
 */
export const usePromptStarterListStyles_unstable = (state: PromptStarterListState): PromptStarterListState => {
  'use no memo';

  const { isExpanded } = state;

  const rootClassName = useRootClassName();
  const styles = useStyles();
  state.root.className = mergeClasses(promptStarterListClassNames.root, rootClassName, state.root.className);
  state.gridWrapper.className = mergeClasses(
    promptStarterListClassNames.gridWrapper,
    styles.gridWrapper,
    state.gridWrapper.className,
  );
  if (state.actions) {
    state.actions.className = mergeClasses(
      promptStarterListClassNames.actions,
      styles.actions,
      state.actions.className,
    );
  }
  if (state.expandButton) {
    state.expandButton.className = mergeClasses(
      promptStarterListClassNames.expandButton,
      styles.expandButton,
      isExpanded && styles.expandButtonExpanded,
      state.expandButton.className,
    );
  }

  return state;
};
